/**
 * @class ProductBreadcrumbs
 * @extends HTMLElement
 * @description A custom element for displaying product breadcrumbs
 */
class ProductBreadcrumbs extends HTMLElement {
  /**
   * @constructor
   * @description The constructor for the ProductBreadcrumbs class
   */
  constructor() {
    super();

  }

  /**
   * @description The connectedCallback for the ProductBreadcrumbs class
   */
  connectedCallback(){
    this.init();
  }

  /**
   * @description The init method for the ProductBreadcrumbs class
   */
  init(){
    // grab our previous page and product collections.
    this.previousPage = document.referrer;
    this.collections = this.dataset.collections.split(',');
    this.priorityNav = this.dataset.priorityNav;
    // nav link elements that will not be used for breadcrumb creation (comma-separated list)
    this.excludedLinkSelectors = '.mega-link__image-link';
    this.topLevelNavClass = 'menu-item--level-top';

    // check for stored previous page on active link list item
    this.activeLink = document.querySelector('[data-keep-navigation].active');
    if (this.activeLink) {
      const activeLinkId = this.activeLink.dataset.keepNavigation;
      const storedPreviousPage = sessionStorage.getItem(`keepNavigation-${activeLinkId}`);
      if (storedPreviousPage) {
        this.previousPage = storedPreviousPage;
      }
    }

    this.setHTML();

    // show the breadcrumb element
    this.style.opacity = 1;
  }

  /**
   * @description The setHTML method for the ProductBreadcrumbs class
   */
  setHTML(){
    this.replaceChildren();
    let breadcrumbsHTML = ``;
    // if we just came from a collection, make sure the collection is in the this.collections array
    let previousPageCollectionIsProductListing = false;
    if(this.previousPage.indexOf('/collections/') >= 0) {
      // grab the collection handle from the previous page, including /collections/, and remove any query params
      const collectionHandle = `/collections/${this.previousPage.split('/collections/')[1].split('?')[0]}`;
      if(collectionHandle) {
        // check if the collection handle is in the this.collections array
        if(this.collections.includes(collectionHandle)) {
          previousPageCollectionIsProductListing = true;
        }
      }
    }
    // if we just came from a collection, set up the breadcrumbs relative to that source if it's in the navigation.
    if(previousPageCollectionIsProductListing) {
      breadcrumbsHTML = this.setPreviousPageBreadcrumbs();
    }
    // if we still do not have breadcrumbs html, and we have a priority nav menu assigned, set up the breadcrumbs based on that menu if it's in the navigation.
    if (this.priorityNav && !breadcrumbsHTML) {
      const priorityNavLink = this.getPriorityNavLink();
      if (priorityNavLink) {
        breadcrumbsHTML = this.buildNavigationBreadcrumbs(priorityNavLink);
      }
    }
    // if we still do not have breadcrumbs html, and we have collections in the this.collections array, set up the breadcrumbs based on the collections if they're in the navigation.
    if(!breadcrumbsHTML) {
      for (const collection of this.collections) {
        if (document.querySelector(this.getNavItemSelector(collection))) {
          const collectionNavItem = this.getLowestLevelNavLink(collection);
          if (collectionNavItem) {
            breadcrumbsHTML = this.buildNavigationBreadcrumbs(collectionNavItem);
            break;
          }
        }
      }
    }
    // at this point, if we still do not have breadcrumbs html, we're just going to show the home link.
    if(!breadcrumbsHTML) {
      breadcrumbsHTML = `<a href="/">Home</a>`;
    }
    // if we have breadcrumbs, append them to the product breadcrumbs element
    if(breadcrumbsHTML) {
      this.appendChild(window.Utilities.createElementsFromHTML(`<div>${breadcrumbsHTML}</div>`));
    }
  }

  /**
   * @description The getPriorityNavSelector method for the ProductBreadcrumbs class
   * @returns {string} The selector for the priority nav menu
   */
  getPriorityNavSelector() {
    return `.header__inline-menu [data-target-menu="${this.priorityNav}"] a:not(${this.excludedLinkSelectors})`;
  }

  /**
   * @description The getPriorityNavLink method for the ProductBreadcrumbs class
   * @returns {Element} The link element for the priority nav menu
   */
  getPriorityNavLink() {
    // Find the best match based on the order of nav items within product's priority nav menu
    const priorityMenu = document.querySelectorAll(this.getPriorityNavSelector());

    return [...priorityMenu].find((anchor) => {
      const navLink = anchor.getAttribute("href");
      return this.collections.includes(navLink);
    });
  }

  /**
   * @description The getNavItemSelector method for the ProductBreadcrumbs class
   * @param {string} handle The handle of the nav item
   * @returns {string} The selector for the nav item
   */
  getNavItemSelector(handle) {
    return `.header__inline-menu li a[href="${handle}"]:not(${this.excludedLinkSelectors})`;
  }

  /**
   * @description The getLowestLevelNavLink method for the ProductBreadcrumbs class
   * @param {string} linkHandle The handle of the nav item
   * @returns {Element} The link element for the nav item
   */
  getLowestLevelNavLink(linkHandle) {
    const navLinks = document.querySelectorAll(this.getNavItemSelector(linkHandle));
    const sortedLinks = [...navLinks];
    // Sort the links so top level links in nav layout are last in array
    sortedLinks.sort((firstLink, secondLink) => {
      // Links are nested
      if (secondLink.contains(firstLink)) return 1;
      if (firstLink.contains(secondLink)) return -1;

      // Links are not nested, but one of the links is top level
      const isFirstTopLevel = firstLink.classList.contains(this.topLevelNavClass);
      const isSecondTopLevel = secondLink.classList.contains(this.topLevelNavClass);
      if (isFirstTopLevel !== isSecondTopLevel) return isFirstTopLevel ? 1 : -1;

      return 0;
    });
    // Return the first non-falsy link
    return sortedLinks.find(el => !!el);
  }

  /**
   * @description The setPreviousPageBreadcrumbs method for the ProductBreadcrumbs class
   * @returns {string} The HTML for the previous page breadcrumbs
   */
  setPreviousPageBreadcrumbs() {
    let previousPageURL = this.previousPage.split('?')[0].replace(location.origin, '');
    let navItem = this.getLowestLevelNavLink(previousPageURL);
    if (navItem) {
      return this.buildNavigationBreadcrumbs(navItem);
    }
  }

  /**
   * @description The createCrumbLink method for the ProductBreadcrumbs class
   * @param {Element} navEl The nav element
   * @param {boolean} endOfCrumbs Whether the nav element is the last element in the breadcrumb
   * @returns {string} The HTML for the crumb link
   */
  createCrumbLink(navEl, endOfCrumbs = false) {
    let crumbLink = `<a href="${navEl?.getAttribute('href') ? navEl?.getAttribute('href') : '/'}">${navEl?.innerHTML.trim()}</a>`;

    // If not last element in breadcrumb, append trailing slash:
    if (!endOfCrumbs) crumbLink += '<span>&nbsp;/&nbsp;</span>';

    return crumbLink;
  }

  /**
   * @description The buildNavigationBreadcrumbs method for the ProductBreadcrumbs class
   * @param {Element} navItem The nav element
   * @returns {string} The HTML for the breadcrumb
   */
  buildNavigationBreadcrumbs(navItem) {
    let breadcrumbsHTML = ``;
    if (!navItem) return breadcrumbsHTML;

    // check level in the navigation
    if(!navItem?.classList.contains(this.topLevelNavClass)) {
      // Find top level nav element for first crumb
      let level1Item = navItem.closest('details-disclosure').querySelector(`.${this.topLevelNavClass}`);
      if(level1Item){
        breadcrumbsHTML += this.createCrumbLink(level1Item);
      }

      // If nav item is third level, get sub-heading crumb
      if(navItem?.classList.contains('menu-item--level-3')) {
        let level2Item = navItem.closest('details').querySelector('summary a');
        if(level2Item){
          breadcrumbsHTML += this.createCrumbLink(level2Item);
        }
      }
    }

    // Always include actual nav item as last crumb
    breadcrumbsHTML += this.createCrumbLink(navItem, true);

    return breadcrumbsHTML;
  }
}

customElements.define('product-breadcrumbs', ProductBreadcrumbs)
